import type { TemplateDoc } from '../template';
import { sceneTimeline } from './compile';
import { docMotionSchema } from './schema';

/**
 * Music analysis for beat-synced animation: a coarse waveform for the timeline and the tempo and
 * phase of the beat grid. Pure functions over mono PCM samples, so the server (at upload) and
 * tests run the same code.
 */

export interface BeatInfo {
  /** Beats per minute, or null when the track has no steady pulse. */
  bpm: number | null;
  /** Time of the first beat in the track, ms. */
  beatOffset: number;
}

/** Peak level of `count` equal slices of the track, 0..1 (normalised to the loudest slice). */
export function waveformPeaks(samples: Float32Array, count = 400): number[] {
  const n = Math.max(1, Math.min(count, samples.length));
  const size = samples.length / n;
  const out: number[] = [];
  let max = 0;
  for (let i = 0; i < n; i++) {
    let p = 0;
    const end = Math.min(samples.length, Math.floor((i + 1) * size));
    for (let j = Math.floor(i * size); j < end; j++) p = Math.max(p, Math.abs(samples[j]));
    out.push(p);
    max = Math.max(max, p);
  }
  return out.map((p) => (max > 0 ? Math.round((p / max) * 100) / 100 : 0));
}

/**
 * Tempo by autocorrelation of an onset envelope (rises of loudness), preferring tempos around
 * 120 BPM as people do, then the phase that lines the grid up with the strongest onsets.
 * Looks at the first 60 s at most.
 */
export function detectBeats(samples: Float32Array, rate: number): BeatInfo {
  const hop = Math.max(1, Math.round(rate / 100)); // 10 ms frames
  const frames = Math.min(Math.floor(samples.length / hop), 6000);
  if (frames < 300) return { bpm: null, beatOffset: 0 };
  const energy = new Float32Array(frames);
  for (let f = 0; f < frames; f++) {
    let e = 0;
    for (let j = f * hop; j < (f + 1) * hop; j++) e += samples[j] * samples[j];
    energy[f] = Math.log1p((e / hop) * 1000);
  }
  const onset = new Float32Array(frames);
  for (let f = 1; f < frames; f++) onset[f] = Math.max(0, energy[f] - energy[f - 1]);
  let mean = 0;
  for (const v of onset) mean += v;
  mean /= frames;
  for (let f = 0; f < frames; f++) onset[f] = Math.max(0, onset[f] - mean);

  const fps = rate / hop;
  const minLag = Math.floor((60 / 200) * fps);
  const maxLag = Math.ceil((60 / 60) * fps);
  const acf = (lag: number) => {
    let s = 0;
    for (let f = lag; f < frames; f++) s += onset[f] * onset[f - lag];
    return s / (frames - lag);
  };
  let zero = 0;
  for (const v of onset) zero += v * v;
  zero /= frames;
  if (zero <= 0) return { bpm: null, beatOffset: 0 };
  let best = 0;
  let bestLag = 0;
  for (let lag = minLag; lag <= maxLag; lag++) {
    const bpm = (60 * fps) / lag;
    const weight = Math.exp(-0.5 * Math.pow(Math.log2(bpm / 120) / 0.9, 2));
    // A beat at `lag` also shows at twice the lag: reward periodic evidence
    const s = (acf(lag) + 0.5 * acf(Math.min(frames - 1, lag * 2))) * weight;
    if (s > best) {
      best = s;
      bestLag = lag;
    }
  }
  // Weak periodicity: no pulse (speech, ambient)
  if (!bestLag || acf(bestLag) / zero < 0.08) return { bpm: null, beatOffset: 0 };
  // Fine search of period and phase together: a beat grid that collects the most onset
  // strength over the whole excerpt (a small tempo error would drift the grid off the beat)
  const comb = (period: number, phase: number) => {
    let sum = 0;
    for (let t = phase; t < frames - 1; t += period) {
      const k = Math.floor(t);
      const f = t - k;
      sum += onset[k] * (1 - f) + onset[k + 1] * f;
    }
    return sum;
  };
  let period = bestLag;
  let bestPhase = 0;
  let bestSum = -1;
  for (let p = bestLag - 1.5; p <= bestLag + 1.5; p += 0.02) {
    for (let ph = 0; ph < p; ph += 0.5) {
      const sum = comb(p, ph);
      if (sum > bestSum) {
        bestSum = sum;
        period = p;
        bestPhase = ph;
      }
    }
  }
  const bpm = Math.round(((60 * fps) / period) * 10) / 10;
  return { bpm, beatOffset: Math.round((bestPhase / fps) * 1000) };
}

/**
 * Beat times (ms on the video timeline) of a track placed at `start` with `trimStart` cut from
 * its head. The grid goes on before the music starts, so moves can land on it anywhere.
 */
export function beatTimes(
  audio: { bpm?: number | null; beatOffset?: number; trimStart?: number; start?: number },
  duration: number,
  every = 1,
): number[] {
  if (!audio.bpm) return [];
  const step = (60_000 / audio.bpm) * every;
  // First beat at or after the cut point, shifted onto the video timeline
  const shift = (audio.start ?? 0) + (audio.beatOffset ?? 0) - (audio.trimStart ?? 0);
  const first = ((shift % step) + step) % step;
  const out: number[] = [];
  for (let t = first; t <= duration + 1; t += step) out.push(Math.round(t));
  return out;
}

/** Beat length of a track, ms (0 without a tempo). */
export function beatMs(audio: { bpm?: number | null } | null | undefined): number {
  return audio?.bpm ? 60_000 / audio.bpm : 0;
}

/**
 * Put the choreography on the beat of the music: scene lengths become whole beats, and every
 * start (enters, accents, graphics, camera moves) and every exit end lands on the nearest half
 * beat. Durations of the moves themselves are kept.
 */
export function syncToBeats(doc: TemplateDoc): TemplateDoc {
  const audio = doc.motion?.audio;
  const beat = beatMs(audio);
  if (!doc.motion || !audio || !beat) return doc;
  const motion = docMotionSchema.parse(structuredClone(doc.motion));
  for (const s of motion.scenes)
    s.duration = Math.max(Math.round(beat * 2), Math.round(Math.round(s.duration / beat) * beat));
  if (!motion.scenes.length)
    motion.duration = Math.min(
      60_000,
      Math.max(Math.round(beat * 4), Math.round(Math.round(motion.duration / beat) * beat)),
    );
  const { scenes, duration } = sceneTimeline(motion);
  const grid = beatTimes(audio, duration + beat, 0.5);
  const snap = (t: number) => {
    let best = t;
    let d = Infinity;
    for (const g of grid)
      if (Math.abs(g - t) < d) {
        d = Math.abs(g - t);
        best = g;
      }
    return best;
  };
  const byId = new Map(scenes.map((s) => [s.id, s]));
  const layers = doc.layers.map((l) => {
    const m = l.motion;
    if (!m) return l;
    const scene = m.scene ? byId.get(m.scene) : undefined;
    const from = scene?.start ?? 0;
    const to = scene?.end ?? duration;
    const next = structuredClone(m);
    if (next.enter) next.enter.delay = Math.max(0, snap(from + next.enter.delay) - from);
    if (next.emphasis) next.emphasis.delay = Math.max(0, snap(from + next.emphasis.delay) - from);
    if (next.exit) next.exit.delay = Math.max(0, to - Math.min(to, snap(to - next.exit.delay)));
    return { ...l, motion: next };
  });
  motion.fx = motion.fx.map((f) => ({ ...f, start: Math.max(0, snap(f.start)) }));
  motion.camera = motion.camera.map((c) => ({ ...c, start: Math.max(0, snap(c.start)) }));
  return { ...doc, motion, layers };
}

/** Loudness of the music at `ms` of a video `duration` ms long: volume with fade in and out. */
export function audioGain(
  audio: { volume?: number; fadeIn?: number; fadeOut?: number },
  ms: number,
  duration: number,
): number {
  const v = audio.volume ?? 1;
  const fin = audio.fadeIn ?? 0;
  const fout = audio.fadeOut ?? 0;
  const a = fin > 0 ? Math.min(1, Math.max(0, ms / fin)) : 1;
  const b = fout > 0 ? Math.min(1, Math.max(0, (duration - ms) / fout)) : 1;
  return Math.max(0, Math.min(1, v * a * b));
}
